import React, { useState } from "react";
import Suggestion, { SuggestionAction } from "./suggestion";
import NotebookBlockSelector from "organisms/notebook-block-selector/notebook-block-selector";
import ToolCallRecord from "models/view-models/conversations/tool-call-record";
import { ToolCallStatusAction } from "models/enumerations/conversations/tool-call-interaction-action";
import { BlockDestinationRecord } from "models/view-models/populated-blocks/block-destination-record";
import { CreatePopulatedBlockRequestRecord } from "models/view-models/populated-blocks/create-populated-block-request-record";
import PopulatedBlocksService from "utilities/services/populated-blocks/populated-blocks-service";
import { useHistory, useLocation, useRouteMatch } from "react-router";
import { RouteUtils } from "utilities/route-utils";
import { siteMap } from "internal-sitemap";
import { ToastManager } from "utilities/toast/toast-manager";
import { useConversationState } from "utilities/atoms/conversations/current-conversation/use-conversation-state.hook";
import { useConversationActions } from "utilities/atoms/conversations/current-conversation/use-conversation-actions.hook";
import { UrlParamNames } from "constants/url-param-names";
import { useEvents } from "utilities/contexts/events/events-provider";
import { ToolCallEventActions } from "models/constants/events/chat-event-actions";
import AcceptedToolCallSuggestion from "./accepted-tool-call-suggestion";
import RejectedToolCallSuggestion from "./rejected-tool-call-suggestion";
import ErrorToolCallSuggestion from "./error-tool-call-suggestion";
import { t } from "utilities/localization-utils";
import Icon from "atoms/icons/icon";
import { Icons } from "atoms/constants/icons";
import { IconSizes } from "atoms/constants/icon-sizes";
import { CASI_QUERY_PARAM_NAME } from "utilities/hooks/domain/chat/use-chat-modal";

const BASE_CLASS = "c-suggestion";

interface NotebookToolCallSuggestionProps {
    toolCall: ToolCallRecord;
    messageId: string;
}

const NotebookToolCallSuggestion = ({
    toolCall,
    messageId,
}: NotebookToolCallSuggestionProps) => {
    const { currentConversation } = useConversationState();
    const { updateToolCallStatus } = useConversationActions();
    const { createToolCallEvent } = useEvents();
    const { push, replace } = useHistory();
    const { pathname, search } = useLocation();
    const routeMatch = useRouteMatch<{ pageId: string }>(
        siteMap.notebooks.notebook.page
    );

    const [goToNotebooksRoute, setGoToNotebooksRoute] = useState("");
    const [routeText, setRouteText] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [isCurrentPage, setIsCurrentPage] = useState(false);
    const [createdBlockId, setCreatedBlockId] = useState<number>();

    const eventSessionId = currentConversation?.id;
    const saveSupportingText = t("casi-checklist-creation-save-text");

    const completeToolCall = async () => {
        try {
            await updateToolCallStatus({
                messageId: messageId,
                toolCallId: toolCall.id,
                status: ToolCallStatusAction.Completed,
            });
        } catch (error) {
            ToastManager.error(t("oopsErrorOccurred"));
        }
    };

    const addToNotebook: SuggestionAction = {
        label: t("add-to-notebook"),
        onClick: () =>
            updateToolCallStatus({
                messageId: messageId,
                toolCallId: toolCall.id,
                status: ToolCallStatusAction.InProgress,
            }),
    };
    const dismiss: SuggestionAction = {
        label: t("not-now"),
        onClick: () => handleDismiss(),
    };
    const confirmNavigationToNotebook: SuggestionAction = {
        label: t("take-me-to-notebooks"),
        onClick: () => handleRouteToNotebooks(),
    };
    const declineAndCompleteInteraction: SuggestionAction = {
        label: t("no-thanks"),
        onClick: completeToolCall,
    };
    const goToNewBlock = async () => {
        await completeToolCall();
        const currentParams =
            RouteUtils.queryStringToObject<Record<string, unknown>>(search);
        delete currentParams[CASI_QUERY_PARAM_NAME];
        replace(
            RouteUtils.appendQueryParams(pathname, {
                ...currentParams,
                editMode: true,
                activeBlockId: createdBlockId,
            })
        );
    };
    const confirmGoToNewBlock: SuggestionAction = {
        label: "Go to new block",
        onClick: goToNewBlock,
    };

    const handleRetry = () => {
        setError(null);
        updateToolCallStatus({
            messageId: messageId,
            toolCallId: toolCall.id,
            status: ToolCallStatusAction.InProgress,
        });
        if (toolCall?.id && eventSessionId) {
            createToolCallEvent(
                toolCall.id,
                ToolCallEventActions.ChecklistCreateFailed
            );
        }
    };

    const { create } = PopulatedBlocksService.useCreate();

    const checkIfCurrentPage = (pageId: number | undefined) => {
        if (routeMatch == null || pageId == null) {
            return false;
        }

        return routeMatch.params.pageId === pageId.toString();
    };

    const handleSubmit = async (destination: BlockDestinationRecord) => {
        const block = toolCall.getArgumentsRecord().toPopulatedBlock();
        const request = new CreatePopulatedBlockRequestRecord({
            destination,
            block,
        });
        try {
            const createResponse = await create(request);
            await updateToolCallStatus({
                messageId: messageId,
                toolCallId: toolCall.id,
                status: ToolCallStatusAction.Accepted,
            });

            // Populates route with create response
            // To be used later in Go To Notebook Button
            const route = RouteUtils.getUrl(siteMap.notebooks.notebook.page, {
                notebookId: createResponse.resultObject?.notebookId,
                pageId: createResponse.resultObject?.pageId,
            });

            const isCurrentPage = checkIfCurrentPage(
                createResponse.resultObject?.pageId
            );
            setCreatedBlockId(createResponse.resultObject?.blockId);
            if (isCurrentPage) {
                setIsCurrentPage(true);
                const currentParams =
                    RouteUtils.queryStringToObject<Record<string, unknown>>(
                        search
                    );
                replace(
                    RouteUtils.appendQueryParams(pathname, {
                        ...currentParams,
                        fetchAllBlocks: true,
                    })
                );
            }

            const routeWithQueryParams = RouteUtils.appendQueryParams(route, {
                editMode: true,
                activeBlockId: createResponse.resultObject?.blockId,
                fetchAllBlocks: true,
                [UrlParamNames.ConversationId]: currentConversation?.id,
            });
            if (routeWithQueryParams) {
                setGoToNotebooksRoute(routeWithQueryParams);
                setRouteText(
                    createResponse.resultObject?.getFriendlyRoute() ?? ""
                );
            }
        } catch (error) {
            setError("I was unable to create the new checklist.");
            ToastManager.error("Error handling submit.");

            if (toolCall?.id && eventSessionId) {
                createToolCallEvent(
                    toolCall.id,
                    ToolCallEventActions.ChecklistCreateFailed
                );
            }
        }
    };
    const handleRouteToNotebooks = async () => {
        updateToolCallStatus({
            messageId: messageId,
            toolCallId: toolCall.id,
            status: ToolCallStatusAction.Completed,
        });
        if (goToNotebooksRoute) {
            push(goToNotebooksRoute);
        }
    };

    const handleDismiss = async () => {
        await updateToolCallStatus({
            messageId: messageId,
            toolCallId: toolCall.id,
            status: ToolCallStatusAction.Dismissed,
        });
    };

    if (error) {
        return <ErrorToolCallSuggestion handleRetry={handleRetry} />;
    }
    switch (toolCall.status) {
        case ToolCallStatusAction.Completed:
            return <></>;
        case ToolCallStatusAction.InProgress:
            return <NotebookBlockSelector onSubmit={handleSubmit} />;
        case ToolCallStatusAction.Dismissed:
            return (
                <RejectedToolCallSuggestion
                    onUndo={() =>
                        updateToolCallStatus({
                            messageId: messageId,
                            toolCallId: toolCall.id,
                            status: ToolCallStatusAction.Received,
                        })
                    }
                />
            );
        case ToolCallStatusAction.Accepted:
            return (
                <AcceptedToolCallSuggestion
                    primaryAction={
                        isCurrentPage
                            ? confirmGoToNewBlock
                            : confirmNavigationToNotebook
                    }
                    secondaryAction={declineAndCompleteInteraction}
                    route={goToNotebooksRoute}
                    routeText={routeText}
                    isCurrentPage={isCurrentPage}
                />
            );
        case ToolCallStatusAction.Received:
        default:
            return (
                <Suggestion
                    supportingText={saveSupportingText}
                    primaryAction={addToNotebook}
                    secondaryAction={dismiss}>
                    {(supportingText) => {
                        return (
                            <>
                                <header className={`${BASE_CLASS}__header`}>
                                    <span className={`${BASE_CLASS}__tag`}>
                                        <Icon
                                            type={Icons.Sparkle}
                                            size={IconSizes.Small}
                                            cssClassName={`${BASE_CLASS}__tag-icon`}
                                        />
                                        <span>Add to Notebook</span>
                                    </span>
                                </header>
                                <div className={`${BASE_CLASS}__content`}>
                                    <span>{supportingText}</span>
                                </div>
                            </>
                        );
                    }}
                </Suggestion>
            );
    }
};

export default NotebookToolCallSuggestion;
